import { Edit01Icon } from '@langchain/untitled-ui-icons';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';

import groupBy from 'lodash/groupBy';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { InformationalBanner } from '@/components/InformationalBanner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { IconButton } from '@/design-system';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useCurrentOrganization,
  useOrgUserConfiguredUsageLimits,
  useOrgWorkspaces,
} from '@/hooks/useSwr';
import WorkspaceIcon from '@/icons/WorkspaceIcon.svg?react';
import { TTLSetting, TenantSchema, TraceTTLTier } from '@/types/schema';

import { getCostForTraces } from '../utils/getCostForTraces';
import { getLimitTotals, isNil } from '../utils/getLimitTotals';
import { currencyFormatter, formatter } from '../utils/helpers';
import { useConfigureOrgTTLSettings } from '../utils/ttlSettings.util';
import { useConfigureUsageLimit } from '../utils/usageLimitSetting.utils';
import { CondensedDataRetentionSetting } from './CondensedDataRetentionSetting';

const CostEstimate = ({
  maxCostEstimate,
}: {
  maxCostEstimate: number | null;
}) => {
  return (
    <>
      <div className="inline-flex items-start justify-start self-stretch rounded-lg bg-background pl-2">
        <div className="flex h-11 shrink grow basis-0 items-center justify-start gap-2 py-2.5">
          <div className="flex h-6 shrink grow basis-0 items-center justify-start gap-2">
            <div className="text-base font-normal leading-normal text-tertiary">
              $
            </div>
            {maxCostEstimate !== null && (
              <div className="shrink grow basis-0">
                {formatter.format(maxCostEstimate)}
              </div>
            )}
          </div>
        </div>
      </div>
      {maxCostEstimate === null && (
        <span className="text-sm">
          Configure total trace limit to see cost estimates
        </span>
      )}
    </>
  );
};

const UsageLimitSetting = (
  props: ReturnType<typeof useConfigureUsageLimit>
) => {
  const {
    isLoading,
    totalLimitError,
    setTotalLimitText,
    totalLimitText,
    maxCostEstimate,
    pricePerTrace,
  } = props;

  const showTTLUI = useInstanceFlag(InstanceFlags.show_ttl_ui);

  if (isLoading) {
    return null;
  }
  if (!showTTLUI) {
    // return legacy usage limit setting
    return (
      <div className="grid gap-2">
        <div className="text-base font-semibold text-ls-black">
          Set monthly trace usage limits
        </div>
        <FormControl error={!!totalLimitError}>
          <Input
            value={totalLimitText}
            placeholder="e.g 200"
            startDecorator={isLoading && <CircularProgress size="sm" />}
            endDecorator="Traces"
            disabled={isLoading}
            sx={{ width: 456, height: 44 }}
            onChange={(e) => {
              setTotalLimitText(e.target.value);
            }}
          />
          <FormHelperText>
            {totalLimitError && <span>{totalLimitError}</span>}
          </FormHelperText>
        </FormControl>
        <FormHelperText>
          {totalLimitError ? (
            <span>{totalLimitError}</span>
          ) : maxCostEstimate !== null ? (
            <span>
              Estimated to be{' '}
              <span className="font-bold">
                {currencyFormatter.format(maxCostEstimate)}
              </span>{' '}
              on your current pricing plan. Calculated at ${pricePerTrace} per
              trace.
            </span>
          ) : (
            <span>Calculated at ${pricePerTrace} per trace.</span>
          )}
        </FormHelperText>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-stretch gap-4">
      <div className="text-lg font-semibold">Configure usage limits</div>
      <InformationalBanner className="mb-0" noIcon>
        <div className="text-sm font-normal">
          Features that automatically extend data retention will not be
          accessible once the extended data retention usage limit is reached.
          Learn more{' '}
          <Link
            to="https://docs.langchain.com/langsmith/administration-overview#usage-limits"
            target="_blank"
          >
            <span className="underline">here</span>
          </Link>
        </div>
      </InformationalBanner>
      <WorkspaceLimitSetting {...props} />
    </div>
  );
};

const WorkspaceLimitSetting = ({
  isLoading,
  totalLimitError,
  longlivedLimitError,
  setTotalLimitText,
  setLonglivedLimitText,
  totalLimitText,
  longlivedLimitText,
  maxCostEstimate,
}: ReturnType<typeof useConfigureUsageLimit>) => {
  const { isEnterprise } = useCurrentTier();
  return (
    <>
      <FormControl error={!!totalLimitError}>
        <FormLabel>Total trace monthly limit</FormLabel>
        <Input
          value={totalLimitText}
          placeholder="e.g 200"
          startDecorator={isLoading && <CircularProgress size="sm" />}
          disabled={isLoading}
          sx={{ width: 456, height: 44 }}
          onChange={(e) => {
            setTotalLimitText(e.target.value);
          }}
        />

        <FormHelperText>
          {totalLimitError && <span>{totalLimitError}</span>}
        </FormHelperText>
      </FormControl>

      <FormControl error={!!longlivedLimitError}>
        <FormLabel>Extended data retention traces monthly limit</FormLabel>
        <Input
          value={longlivedLimitText}
          placeholder="e.g 200"
          startDecorator={isLoading && <CircularProgress size="sm" />}
          disabled={isLoading}
          sx={{ width: 456, height: 44 }}
          onChange={(e) => {
            setLonglivedLimitText(e.target.value);
          }}
        />
        <FormHelperText>
          {longlivedLimitError && <span>{longlivedLimitError}</span>}
        </FormHelperText>
      </FormControl>

      {!isEnterprise && (
        <div className="flex max-w-md flex-col gap-2">
          <div className="text-sm text-ls-black">Max monthly cost</div>
          {!totalLimitError && !longlivedLimitError ? (
            <CostEstimate maxCostEstimate={maxCostEstimate} />
          ) : (
            <span className="text-sm">Fix errors to see cost estimates</span>
          )}
        </div>
      )}
    </>
  );
};

const WorkspaceUsageModal = (props: {
  workspaceId: string;
  workspaceName: string;
  children: (props: { onClick: () => void }) => ReactNode;
  ttl: TTLSetting | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const configureUsageLimitProps = useConfigureUsageLimit({
    workspaceId: props.workspaceId,
  });

  const {
    handleTTLChange,
    isMutating,
    isLoading: isTTLLoading,
  } = useConfigureOrgTTLSettings();

  const [ttl, setTTL] = useState<TraceTTLTier | null>(
    props.ttl?.default_trace_tier ?? null
  );
  const [applyToAllProjects, setApplyToAllProjects] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
  };

  const error = configureUsageLimitProps.upsertUserDefinedLimit.error;

  const dataRetention = (
    <FormControl>
      <FormLabel>Data retention policy</FormLabel>
      <CondensedDataRetentionSetting ttl={ttl} setTTL={setTTL} />
      <Checkbox
        checked={applyToAllProjects}
        onChange={(e) => setApplyToAllProjects(e.target.checked)}
        label="Apply to existing projects"
        sx={{ fontSize: '14px', mt: 1 }}
      />
    </FormControl>
  );

  const buttons = (
    <div className="mt-4 flex justify-end gap-2">
      <Button
        color="neutral"
        variant="outlined"
        onClick={() => {
          setIsOpen(false);
          configureUsageLimitProps.reset();
          setTTL(props.ttl?.default_trace_tier ?? null);
        }}
      >
        Cancel
      </Button>
      <Button
        loading={isMutating || configureUsageLimitProps.isMutating}
        color="primary"
        onClick={async () => {
          await configureUsageLimitProps.saveLimits();

          if (ttl != null) {
            await handleTTLChange(props.workspaceId, ttl, applyToAllProjects);
          }
          setIsOpen(false);
        }}
      >
        Save
      </Button>
    </div>
  );

  const configureUsage =
    configureUsageLimitProps.isLoading || isTTLLoading ? (
      <LinearProgress />
    ) : (
      <>
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2 ">
            <WorkspaceIcon className="h-4 w-4 flex-none" />
            <span className="line-clamp-1 max-w-[550px] overflow-hidden text-ellipsis text-lg font-semibold">
              {props.workspaceName} workspace usage configurations
            </span>
          </div>

          <div className="flex w-full flex-col gap-4">
            <WorkspaceLimitSetting {...configureUsageLimitProps} />
            {dataRetention}
          </div>
          {error != null && <ExpandableErrorAlert error={error} />}
        </div>
        {buttons}
      </>
    );

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalDialog
          variant="outlined"
          sx={{ width: 550, paddingX: 5, paddingY: 4 }}
        >
          <ModalClose size="lg" />
          {configureUsage}
        </ModalDialog>
      </Modal>
      {props.children?.({ onClick: () => setIsOpen(true) })}
    </>
  );
};

type WorkspaceRow = {
  w: TenantSchema;
  limitAndCosts: {
    totalLimit: number | null;
    longlivedLimit: number | null;
    maxCost: number | null;
  };
  ttl: TTLSetting | undefined;
};

type WorkspaceRowProps = WorkspaceRow & {
  orgTTLSetting: TTLSetting | undefined;
  isEnterprise: boolean;
};

const WorkspaceUsageRow = ({
  w,
  limitAndCosts,
  isEnterprise,
  ttl,
  orgTTLSetting,
}: WorkspaceRowProps) => {
  const { totalLimit, longlivedLimit, maxCost } = limitAndCosts;
  const { authorize } = usePermissions('organization');

  const ttlLabelMap = {
    longlived: 'Extended',
    shortlived: 'Base',
  };
  return (
    <WorkspaceUsageModal
      key={w.id}
      workspaceId={w.id}
      workspaceName={w.display_name}
      ttl={ttl}
    >
      {({ onClick }) => (
        <TableRow key={w.id}>
          <TableCell className="p-4 pl-5 ">
            <div className="flex items-center gap-2 ">
              <WorkspaceIcon className="h-4 w-4 shrink-0" />
              <div className="line-clamp-1 overflow-hidden text-ellipsis">
                {w.display_name}
              </div>
            </div>
          </TableCell>
          <TableCell className="text-center ">
            {isNil(ttl?.default_trace_tier) ? (
              <Button variant="plain" size="sm" onClick={onClick}>
                {orgTTLSetting?.default_trace_tier
                  ? ttlLabelMap[orgTTLSetting.default_trace_tier]
                  : 'Not set'}
              </Button>
            ) : (
              ttlLabelMap[ttl.default_trace_tier]
            )}
          </TableCell>
          <TableCell className="p-0 text-center">
            {isNil(totalLimit) ? (
              <Button variant="plain" size="sm" onClick={onClick}>
                Not set
              </Button>
            ) : (
              formatter.format(totalLimit)
            )}
          </TableCell>
          <TableCell className="p-0 text-center">
            {isNil(longlivedLimit) ? (
              <Button variant="plain" size="sm" onClick={onClick}>
                Not set
              </Button>
            ) : (
              formatter.format(longlivedLimit)
            )}
          </TableCell>
          {!isEnterprise && (
            <TableCell className="p-4 text-center">
              {isNil(maxCost) ? '' : currencyFormatter.format(maxCost)}
            </TableCell>
          )}

          <TableCell className="p-4">
            <IconButton
              label={
                authorize('organization:manage') ? 'Edit' : 'No permission'
              }
              icon={Edit01Icon}
              onClick={authorize('organization:manage') ? onClick : undefined}
              variant="plain"
              color="secondary"
              size="sm"
            />
          </TableCell>
        </TableRow>
      )}
    </WorkspaceUsageModal>
  );
};

export const WorkspaceUsageTable = () => {
  const orgUsageLimitQuery = useOrgUserConfiguredUsageLimits();
  const { isLoading: usageLimitLoading, data: usageLimits } =
    orgUsageLimitQuery;
  const orgWorkspacesQuery = useOrgWorkspaces();
  const { isLoading: workspaceLoading, data: workspaces } = orgWorkspacesQuery;
  const groupedUsageLimits = groupBy(usageLimits, 'tenant_id');
  const org = useCurrentOrganization();
  const { isEnterprise } = useCurrentTier();

  const { isLoading, allTTLSettings } = useConfigureOrgTTLSettings();
  const orgTTLSetting = allTTLSettings?.find((t) => t.tenant_id == null);
  if (usageLimitLoading || workspaceLoading || org.isLoading || isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }
  const totals = getLimitTotals(
    workspaces,
    groupedUsageLimits,
    org?.data?.tier
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="text-lg font-semibold">
        Workspace usage configurations
      </div>
      <div className="text-sm">
        Set usage limits and data retention policy for each workspace to prevent
        accidental overspend.
      </div>
      <InformationalBanner className="mb-0" noIcon>
        <div className="text-sm font-normal">
          Features that automatically extend data retention will not be
          accessible once the extended data retention usage limit is reached.
          Learn more{' '}
          <Link
            to="https://docs.langchain.com/langsmith/administration-overview#usage-limits"
            target="_blank"
          >
            <span className="underline">here</span>
          </Link>
        </div>
      </InformationalBanner>
      <div className="rounded-lg border border-secondary">
        <Table className="table-fixed ">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%] pl-5">Workspace</TableHead>
              <TableHead className="w-[20%] overflow-hidden text-ellipsis text-center ">
                Data retention policy
              </TableHead>
              <TableHead className="w-[20%] overflow-hidden text-ellipsis text-center ">
                Total trace limit
              </TableHead>
              <TableHead className="w-[20%] overflow-hidden text-ellipsis text-center">
                Extended trace limit
              </TableHead>
              {!isEnterprise && (
                <TableHead className="w-[20%] overflow-hidden text-ellipsis  text-center">
                  Cost estimate
                </TableHead>
              )}
              <TableHead className="w-[10%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workspaces
              .reduce((acc: WorkspaceRow[], w) => {
                const ttl = allTTLSettings?.find((t) => t.tenant_id === w.id);

                const usageLimits = groupedUsageLimits[w.id];
                const totalLimit =
                  usageLimits?.find((s) => s.limit_type === 'monthly_traces')
                    ?.limit_value ?? null;

                const longlivedLimit =
                  usageLimits?.find(
                    (s) => s.limit_type === 'monthly_longlived_traces'
                  )?.limit_value ?? null;

                const cumulativeTotal = acc.reduce((acc, { limitAndCosts }) => {
                  return acc + (limitAndCosts.totalLimit ?? 0);
                }, 0);
                const cumulativeLonglived = acc.reduce(
                  (acc, { limitAndCosts }) => {
                    return acc + (limitAndCosts.longlivedLimit ?? 0);
                  },
                  0
                );
                const maxCost = getCostForTraces(
                  totalLimit,
                  longlivedLimit,
                  org?.data?.tier,
                  cumulativeTotal,
                  cumulativeLonglived
                ).maxCostEstimate;

                return [
                  ...acc,
                  {
                    limitAndCosts: { totalLimit, longlivedLimit, maxCost },
                    w,
                    ttl,
                  },
                ];
              }, [])
              .map(({ w, limitAndCosts, ttl }) => (
                <WorkspaceUsageRow
                  key={w.id}
                  w={w}
                  limitAndCosts={limitAndCosts}
                  isEnterprise={isEnterprise}
                  ttl={ttl}
                  orgTTLSetting={orgTTLSetting}
                />
              ))}
            {!isEnterprise && (
              <TableRow>
                <TableCell className="p-4 pl-5 " colSpan={2}>
                  Total
                </TableCell>

                {totals.total === null ? (
                  <TableCell colSpan={3}>
                    Please set total trace limit for each workspace to see price
                    estimates
                  </TableCell>
                ) : (
                  <>
                    <TableCell className="p-0 text-center">
                      {formatter.format(totals.total)}
                    </TableCell>
                    <TableCell className="p-0 text-center">
                      {totals.longlived === null
                        ? 'Not set'
                        : formatter.format(totals.longlived)}
                    </TableCell>
                    <TableCell className="p-4 text-center">
                      {currencyFormatter.format(totals.maxCost ?? 0)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default UsageLimitSetting;
